import React, { useEffect, useId, useRef, useState } from "react";
import Portal from "../../../utils/Portal";
import useModalA11y from "./useModalA11y";
import styles from "./ConfirmDialog.module.css";

/**
 * Automation-friendly confirm / prompt dialog.
 *
 * Presentational only; state lives in the caller (see ConfirmDialogProvider
 * for the promise-based `useConfirm()` / `usePrompt()` hooks).
 *
 * Props
 *  - open            boolean
 *  - mode            "confirm" (default) | "prompt"
 *  - title, message  strings (message supports "\n" line breaks)
 *  - confirmLabel    default "Confirm"
 *  - cancelLabel     default "Cancel"
 *  - danger          styles the confirm button red
 *  - defaultValue / placeholder / inputLabel / inputType   (prompt mode)
 *  - required        prompt mode: disables confirm while the input is empty
 *  - onConfirm(value)  value is `true` in confirm mode, the input string in prompt mode
 *  - onCancel()
 *  - testId          base data-testid, default "confirm-dialog"
 *
 * Test ids: `${testId}`, `${testId}-backdrop`, `${testId}-title`,
 * `${testId}-message`, `${testId}-input`, `${testId}-confirm`, `${testId}-cancel`.
 */
export default function ConfirmDialog({
  open = false,
  mode = "confirm",
  title = "Are you sure?",
  message = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  defaultValue = "",
  placeholder = "",
  inputLabel = "",
  inputType = "text",
  required = false,
  onConfirm,
  onCancel,
  testId = "confirm-dialog",
}) {
  const isPrompt = mode === "prompt";
  const [value, setValue] = useState(defaultValue);
  const cancelRef = useRef(null);
  const inputRef = useRef(null);
  const ids = useId();
  const titleId = `${ids}-title`;
  const messageId = `${ids}-message`;

  // Reset the prompt value each time the dialog (re)opens.
  useEffect(() => {
    if (open) setValue(defaultValue);
  }, [open, defaultValue]);

  const dialogRef = useModalA11y({
    open,
    onClose: onCancel,
    // Initial focus lands on Cancel so a stray Enter/Space never confirms;
    // prompt mode focuses the input so the user can start typing.
    initialFocusRef: isPrompt ? inputRef : cancelRef,
  });

  if (!open) return null;

  const confirmDisabled = isPrompt && required && !`${value}`.trim();

  const handleConfirm = () => {
    if (confirmDisabled) return;
    if (typeof onConfirm === "function") onConfirm(isPrompt ? value : true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleConfirm();
  };

  return (
    <Portal>
      <div
        className={styles.overlay}
        data-testid={`${testId}-backdrop`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && typeof onCancel === "function") {
            onCancel();
          }
        }}
      >
        <div
          ref={dialogRef}
          className={styles.dialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={message ? messageId : undefined}
          tabIndex={-1}
          data-testid={testId}
          data-state="open"
          data-mode={mode}
          data-danger={danger ? "true" : "false"}
        >
          <form onSubmit={handleSubmit}>
            <h2 id={titleId} className={styles.title} data-testid={`${testId}-title`}>
              {title}
            </h2>
            {message ? (
              <p id={messageId} className={styles.message} data-testid={`${testId}-message`}>
                {message}
              </p>
            ) : null}
            {isPrompt && (
              <div>
                {inputLabel ? (
                  <label className={styles.inputLabel} htmlFor={`${ids}-input`}>
                    {inputLabel}
                  </label>
                ) : null}
                <input
                  ref={inputRef}
                  id={`${ids}-input`}
                  type={inputType}
                  className={styles.input}
                  value={value}
                  placeholder={placeholder}
                  onChange={(event) => setValue(event.target.value)}
                  aria-label={inputLabel || title}
                  data-testid={`${testId}-input`}
                />
              </div>
            )}
            <div className={styles.actions}>
              <button
                ref={cancelRef}
                type="button"
                className={styles.btnSecondary}
                onClick={onCancel}
                data-testid={`${testId}-cancel`}
              >
                {cancelLabel}
              </button>
              <button
                type="submit"
                className={danger ? styles.btnDanger : styles.btnPrimary}
                disabled={confirmDisabled}
                data-testid={`${testId}-confirm`}
              >
                {confirmLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
}
